/**
 * exam-result-summary.ts
 *
 * Spec §4.4 ResultSummary panel — chapter-aggregated breakdown shown
 * after a mock-exam paper concludes (submit / time-out / early-submit).
 *
 * Reads `kp` off each question, looks up its chapter via CURRICULUM, then
 * renders per-chapter accuracy with weak chapters (<70%) highlighted and
 * a "📘 進入修煉殿補強" recommend panel pointing at the worst chapters.
 *
 * Privacy / Architecture invariants:
 * - Pure DOM render (no localStorage / no XP / no Phaser)
 * - Caller (paper-view) handles XP/mistake side-effects BEFORE invoking
 *   this; this module is presentation only
 */

import type { Subject } from '../../shared/types';
import type { PaperResult, PaperPerQuestion } from './paper-view';
import { CURRICULUM } from '../curriculum/index';

interface ChapterStat {
  chapterNum: number;
  chapterTitle: string;
  correct: number;
  total: number;
}

function getSubjectLabel(subject: Subject): string {
  const s = CURRICULUM.find(c => c.subject === subject);
  return s?.label ?? subject;
}

function findChapterForKp(
  subject: Subject,
  kpId: string,
): { chapterNum: number; chapterTitle: string } | null {
  const subjectData = CURRICULUM.find(c => c.subject === subject);
  if (!subjectData) return null;
  for (const ch of subjectData.chapters) {
    if (ch.kps.some(kp => kp.id === kpId)) {
      return { chapterNum: ch.number, chapterTitle: ch.title };
    }
  }
  return null;
}

function groupByChapter(subject: Subject, perQuestion: PaperPerQuestion[]): ChapterStat[] {
  const map = new Map<number, ChapterStat>();
  for (const p of perQuestion) {
    const kp = (p.question as { kp?: string }).kp;
    if (!kp) continue;
    const ch = findChapterForKp(subject, kp);
    if (!ch) continue;
    const existing = map.get(ch.chapterNum) ?? {
      chapterNum: ch.chapterNum, chapterTitle: ch.chapterTitle, correct: 0, total: 0,
    };
    existing.total += 1;
    if (p.correct) existing.correct += 1;
    map.set(ch.chapterNum, existing);
  }
  return Array.from(map.values()).sort((a, b) => a.chapterNum - b.chapterNum);
}

function formatElapsed(elapsedSec: number): string {
  const m = Math.floor(Math.max(0, elapsedSec) / 60);
  const s = Math.floor(Math.max(0, elapsedSec) % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function escapeHtml(s: string): string {
  const d = document.createElement('div');
  d.textContent = s;
  return d.innerHTML;
}

export function renderExamResultSummary(
  container: HTMLElement,
  result: PaperResult,
  subject: Subject,
  awaySeconds: number,
  onDone: () => void,
): void {
  const accuracy = result.totalQuestions === 0
    ? 0
    : Math.round((result.correctCount / result.totalQuestions) * 100);
  const chapters = groupByChapter(subject, result.perQuestion);
  const weakChapters = chapters.filter(c => c.total > 0 && (c.correct / c.total) < 0.7);

  const subjectLabel = getSubjectLabel(subject);
  const elapsed = formatElapsed(result.elapsedSec);

  const root = document.createElement('div');
  root.className = 'exam-result-summary formal-page';

  const chapterRowsHtml = chapters.map(c => {
    const pct = c.total === 0 ? 0 : Math.round((c.correct / c.total) * 100);
    const isWeak = pct < 70;
    const icon = pct === 100 ? '✅' : isWeak ? '🟡' : '✅';
    const cls = isWeak ? 'ers-chapter-row ers-chapter-row--weak' : 'ers-chapter-row';
    const tail = isWeak ? ' ← 弱點' : '';
    return `<li class="${cls}">${icon} 第 ${c.chapterNum} 章 ${escapeHtml(c.chapterTitle)} ${c.correct}/${c.total} (${pct}%)${tail}</li>`;
  }).join('');

  const recommendHtml = weakChapters.length > 0
    ? `<div class="ers-recommend">🎯 推薦補強：${
        weakChapters.map(c => `第 ${c.chapterNum} 章「${escapeHtml(c.chapterTitle)}」`).join(' + ')
      }</div>`
    : '';

  const awayNoteHtml = awaySeconds > 0
    ? `<div class="ers-away-note">⚠ 離場 ${awaySeconds} 秒（僅供參考，未扣分）</div>`
    : '';

  root.innerHTML = `
    <h2 class="ers-title">📊 模擬考結算 — ${escapeHtml(subjectLabel)}</h2>
    <div class="ers-headline">
      <span class="ers-elapsed">⏱ 用時 ${elapsed}</span>
      <span class="ers-score">📝 答對 ${result.correctCount} / ${result.totalQuestions} 題 (${accuracy}%)</span>
    </div>
    <div class="ers-chapters">
      <h3>按章節拆解：</h3>
      <ul class="ers-chapter-list">${chapterRowsHtml}</ul>
    </div>
    ${recommendHtml}
    ${awayNoteHtml}
    <div class="ers-actions">
      <button class="primary-btn ers-done-btn">🏠 回首頁</button>
    </div>
  `;

  container.appendChild(root);

  root.querySelector('.ers-done-btn')!.addEventListener('click', onDone);
}
